// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import Box from 'sail/Box';
import Button, {ButtonLink} from 'sail/Button';
import Group from 'sail/Group';
import {Text} from 'sail/Text';
import {
  useCookies,
  COOKIE_POLICY_URL,
  COOKIE_SETTINGS_PAGE_URL,
} from 'docs/data';
import './CookieBanner.scss';

type CookieBannerMode = 'prompt' | 'dismissed-toast' | 'closed';

const TOAST_DURATION_MS = 5000;

const DismissedToast = () => (
  <Box className="CookieBanner-toast" padding={16}>
    <Text color="white" size={13} lineHeight={16}>
      OK! We'll only set cookies that are absolutely necessary for our systems
      to function.
    </Text>
  </Box>
);

type PromptProps = {
  onAcceptAll: () => mixed,
  onRejectAll: () => mixed,
};

const Prompt = ({onAcceptAll, onRejectAll}: PromptProps) => {
  return (
    <Box className="CookieBanner-prompt" padding={16}>
      <Text color="white" size={13} lineHeight={20}>
        We use cookies to improve your experience and for marketing. Learn more
        in our{' '}
        <a href={COOKIE_POLICY_URL} target="_blank">
          cookie policy
        </a>
        .
      </Text>
      <Box className="CookieBanner-actions">
        <Group
          className="CookieBanner-buttons"
          direction="horizontal"
          spacing={8}
        >
          <Button
            label="Manage cookies"
            color="white"
            size="small"
            href={COOKIE_SETTINGS_PAGE_URL}
            target="_blank"
          />
          <Button
            label="Accept all"
            color="white"
            size="small"
            onClick={onAcceptAll}
          />
        </Group>
        <ButtonLink
          className="CookieBanner-close"
          label="Close"
          icon="delete"
          color="gray"
          size="small"
          onClick={onRejectAll}
        />
      </Box>
    </Box>
  );
};

export const CookieBanner = () => {
  const {cookies} = useCookies();
  const [mode, setMode] = useState<CookieBannerMode>('closed');

  const hideWhenPermissionsAreSet = (permissions) => {
    if (mode !== 'dismissed-toast') {
      setMode('closed');
      cookies.unwatchPermissions(hideWhenPermissionsAreSet);
    }
  };

  useEffect(() => {
    cookies.shouldShowBanner().then((showBanner) => {
      if (showBanner) {
        setMode('prompt');
        cookies.watchPermissions(hideWhenPermissionsAreSet);
      }
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, []);

  const handleAcceptAll = () => {
    cookies.allowAll();
    setMode('closed');
  };

  const handleRejectAll = () => {
    cookies.rejectAll();
    setMode('dismissed-toast');
    setTimeout(() => setMode('closed'), TOAST_DURATION_MS);
  };

  let content = null;

  if (mode === 'dismissed-toast') {
    content = <DismissedToast />;
  } else if (mode === 'prompt') {
    content = (
      <Prompt onAcceptAll={handleAcceptAll} onRejectAll={handleRejectAll} />
    );
  }

  return (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        key={mode}
        className="CookieBanner"
        initial={{opacity: 0, y: 100}}
        animate={{opacity: 1, y: 0}}
        exit={{opacity: 0, y: 100}}
        transition={{type: 'spring', damping: 100, stiffness: 1000}}
      >
        {content}
      </motion.div>
    </AnimatePresence>
  );
};
